const _ = require('span-util/src/_');
const objectUtil = require('span-util/src/object-util');

module.exports.init = function init(logger) {
  function compare(valueA, valueB, comparisonOperator) {
    // TODO: should use a check against the number of expected operands rather than the operator name
    if (valueA === undefined && valueB === undefined && comparisonOperator !== 'not-exists') {
      return false;
    }

    let typedValueA = valueA;
    let typedValueB = valueB;

    if (typeof valueA === 'boolean') {
      if (typeof valueB !== 'boolean') {
        typedValueB = (valueB == 'true'); // eslint-disable-line eqeqeq
      }
    } else if (typeof valueB === 'boolean') {
      if (typeof valueA !== 'boolean') {
        typedValueA = (valueA == 'true'); // eslint-disable-line eqeqeq
      }
    }

    switch (comparisonOperator || 'eq') {
      case 'eq':
        if (_.isArray(typedValueA) && _.isArray(typedValueB)) {
          return _.intersection(typedValueA, typedValueB).length > 0;
        }
        if (_.isArray(typedValueA)) {
          return _.contains(typedValueA, typedValueB);
        }
        if (_.isArray(typedValueB)) {
          return _.contains(typedValueB, typedValueA);
        }
        return typedValueA === typedValueB;
      case 'ne':
        if (_.isArray(typedValueA) && _.isArray(typedValueB)) {
          return _.intersection(typedValueA, typedValueB).length === 0;
        }
        if (_.isArray(typedValueA)) {
          return !_.contains(typedValueA, typedValueB);
        }
        if (_.isArray(typedValueB)) {
          return !_.contains(typedValueB, typedValueA);
        }
        return typedValueA !== typedValueB;
      case 'lt':
        return typedValueA < typedValueB;
      case 'gt':
        return typedValueA > typedValueB;
      case 'lte':
        return typedValueA <= typedValueB;
      case 'gte':
        return typedValueA >= typedValueB;
      case 'contains':
        return _.isArray(typedValueA) && _.contains(typedValueA, typedValueB);
      case 'intersection-not-empty':
        return _.isArray(typedValueA) && _.isArray(typedValueB) && (_.intersection(typedValueA, typedValueB).length > 0);
      case 'intersection-empty':
        return _.isArray(typedValueA) && _.isArray(typedValueB) && (_.intersection(typedValueA, typedValueB).length === 0);
      case 'not-exists':
        return typedValueA === undefined;
      default:
        throw Error(`${comparisonOperator} is not a valid operator`);
    }
  }

  function extractComparisonOperands(fieldMatch, user, newFields, existingEntity) {
    const operands = {
      valueA: undefined,
      valueB: undefined,
      operator: 'eq',
    };

    const fieldValues = [];
    if (fieldMatch.UserField) {
      fieldValues.push(objectUtil.getFieldValue(user, fieldMatch.UserField));
    }
    if (fieldMatch.NewField) {
      fieldValues.push(objectUtil.getFieldValue(newFields, fieldMatch.NewField));
    }
    if (fieldMatch.TargetField) {
      fieldValues.push(objectUtil.getFieldValue(existingEntity, fieldMatch.TargetField));
    }
    if (fieldMatch.FixedValue) {
      fieldValues.push(fieldMatch.FixedValue);
    }

    if (fieldMatch.NotTargetField) {
      if (fieldValues.length !== 1) {
        throw new Error(`Only 2 fields must be provided, but found ${fieldValues.length + 1}`);
      }

      operands.valueA = fieldValues[0]; // eslint-disable-line prefer-destructuring
      operands.valueB = objectUtil.getFieldValue(existingEntity, fieldMatch.NotTargetField);
      operands.operator = 'ne';
    } else {
      if (fieldValues.length > 2) {
        throw new Error(`Only 2 fields must be provided, but found ${fieldValues.length}`);
      }
      operands.valueA = fieldValues[0]; // eslint-disable-line prefer-destructuring
      operands.valueB = fieldValues[1]; // eslint-disable-line prefer-destructuring
    }

    if (fieldMatch.Operator) {
      operands.operator = fieldMatch.Operator;
    }

    return operands;
  }

  function satisfiesFieldMatch(fieldMatch, user, newFields, existingEntity) {
    const operands = extractComparisonOperands(fieldMatch, user, newFields, existingEntity);
    const result = compare(operands.valueA, operands.valueB, operands.operator);

    if (!result) {
      logger.debug(`Field match policy (${JSON.stringify(fieldMatch)}) failed: ${operands.valueA} ${operands.operator} ${operands.valueB}`);
      return false;
    }

    logger.trace(`Field match policy passed: ${operands.valueA} ${operands.operator} ${operands.valueB}`);
    return true;
  }

  function satisfiesUserPolicy(userPolicy, user, newFields, existingEntity, skipTargetMatches) {
    if (userPolicy) {
      if (userPolicy.RoleIdMatch) {
        if (!_.contains(user.RoleIds, userPolicy.RoleIdMatch)) {
          logger.debug(`Role match policy failed. User does not have ${userPolicy.RoleIdMatch} role`);
          return false;
        }

        logger.debug(`User has ${userPolicy.RoleIdMatch} role`);
      }

      if (userPolicy.RoleNameMatch) {
        if (!_.contains(user.RoleNames, userPolicy.RoleNameMatch)) {
          logger.debug(`Role match policy failed. User does not have ${userPolicy.RoleNameMatch} role`);
          return false;
        }

        logger.debug(`User has ${userPolicy.RoleNameMatch} role`);
      }

      if (userPolicy.FieldMatches) {
        const passesAllFieldMatches = _.every(userPolicy.FieldMatches, (f) => {
          if (skipTargetMatches && (_.has(f, 'TargetField') || _.has(f, 'NotTargetField'))) {
            return true;
          }
          return satisfiesFieldMatch(f, user, newFields, existingEntity);
        });

        if (!passesAllFieldMatches) {
          return false;
        }
      }
    }

    return true;
  }

  function satisfiesNewFieldPolicy(newFieldPolicy, user, newFields) {
    if (newFieldPolicy && newFieldPolicy.Allowed) {
      const providedFields = _.keys(newFields);
      const allAllowedFields = ['nonce'].concat(newFieldPolicy.Allowed);
      if (_.difference(providedFields, allAllowedFields).length > 0) {
        logger.debug(`New field policy failed. The policy does not allow the user to edit the following: 
          ${_.difference(providedFields, allAllowedFields).join(', ')}`);
        return false;
      }
    }
    return true;
  }

  function satisfiesPolicy(policy, user, newFields, existingEntity, skipTargetMatches) {
    if (policy.IsBlanketPolicy) {
      return true;
    }

    let userPolicy = policy.User;
    if (!_.has(policy, 'User') && _.has(policy, 'RoleNameMatch') && _.has(policy, 'FieldMatches')) {
      userPolicy = policy;
    }

    if (_.has(policy, 'IsBlanketPolicy') && !policy.IsBlanketPolicy
      && !userPolicy && !_.has(policy, 'NewFields')) {
      return false;
    }

    return satisfiesUserPolicy(userPolicy, user, newFields, existingEntity, skipTargetMatches)
       && satisfiesNewFieldPolicy(policy.NewFields, user, newFields);
  }

  function satisfiesPolicyList(policies, user, newFields, existingEntity, skipTargetMatches) {
    return _.some(policies, p => satisfiesPolicy(p, user, newFields, existingEntity, skipTargetMatches));
  }

  function getSatisfiedPolicy(policies, user, newFields, existingEntity, skipTargetMatches) {
    return _.find(policies, p => satisfiesPolicy(p, user, newFields, existingEntity, skipTargetMatches));
  }

  function getAllSatisfiedPolicies(policies, user, newFields, existingEntity, skipTargetMatches) {
    return _.filter(policies, p => satisfiesPolicy(p, user, newFields, existingEntity, skipTargetMatches));
  }

  return {
    getSatisfiedPolicy,
    getAllSatisfiedPolicies,
    satisfiesPolicyList,
    satisfiesPolicy,
    satisfiesNewFieldPolicy,
    satisfiesUserPolicy,
    satisfiesFieldMatch,
    compare,
  };
};
